import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';

import { TicketUpdateRequest } from 'api/ticket';
import { useApiContext } from 'context/ApiContext';
import { useGlobalContext } from 'context/GlobalContext';
import { KEYS } from 'helpers/react-query.config';

/**
 * @hook
 *
 * useUpdateTicketStatus
 *
 * Hook para actualizar el estado de un ticket.
 *
 * @invalidateQueries
 *
 * Invalida las siguientes queries:
 *
 *  `KEYS.ticket.tickets.detail(ticketId)`
 *  `KEYS.ticket.logbook.list()`
 *  `KEYS.ticket.tickets.getAvailableStatuses()`
 *  `KEYS.ticket.tickets.generalSearch()`
 *
 * @returns {object} Un objeto que contiene el estado y funciones para la mutación de actualización del ticket.
 */

export default function useUpdateTicketStatus() {
  const { ticket: ticketApi } = useApiContext();
  const { showSuccess, showApiError } = useGlobalContext();
  const { t } = useTranslation('global');
  const queryClient = useQueryClient();

  const invalidateQueries = async (ticketId: number) => {
    await Promise.all([
      queryClient.invalidateQueries(KEYS.ticket.tickets.detail(ticketId)),
      queryClient.invalidateQueries(KEYS.ticket.logbook.list()),
      queryClient.invalidateQueries(KEYS.ticket.tickets.getAvailableStatuses()),
      queryClient.invalidateQueries(KEYS.ticket.tickets.generalSearch())
    ]);
  };

  return useMutation(
    (data: TicketUpdateRequest) => ticketApi.tickets.updateTicketStatus(data),
    {
      onSuccess: async ({ data }) => {
        await invalidateQueries(Number(data?.data?.ticketId));
        showSuccess(data?.data?.message || t('success.ticketUpdated'), 3000);
      },
      onError: (error) =>
        showApiError(error, t('common.commonMessages.genericRequestError'))
    }
  );
}
